import { Checkbox, DatePicker, Form, Input, Upload, message, Radio } from "antd";
import React, { useEffect, useState } from "react";
import Button from "../../../../components/common/button";
import colors from "../../../../share/constants/constants";
import Dropdown from "../../../../components/common/dropdown";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { createDealBanner, updateDealBanner } from "../../../../api/banner";
import { uploadImage } from "../../../../api/service";
import { PlusOutlined } from "@ant-design/icons";

dayjs.extend(utc);

const DealBannerForm = ({ defaultValues, isEdit, setIsEdit, setShowForm }) => {
  const [form] = Form.useForm();
  const [image, setImage] = useState(
    defaultValues?.desktopImage && defaultValues?.mobileImage
      ? {
          desktopImage: defaultValues?.desktopImage,
          mobileImage: defaultValues?.mobileImage,
        }
      : {}
  );
  const [showTimer, setShowTimer] = useState(false);
  const [clickActionType, setClickActionType] = useState(
    defaultValues?.clickActionType || null
  );
  let initialValues = {};

  if (isEdit) {
    initialValues = {
      ...defaultValues,
    };
    if (defaultValues?.timer) {
      initialValues.timer = dayjs(defaultValues?.timer);
    }
    if (defaultValues?.clickActionType) {
      initialValues.clickActionType = defaultValues.clickActionType;
    }
  }

  const handleImageUpload = async (event) => {
    try {
      const validImage = beforeUpload(event.file);

      if (!validImage.isJpgOrPng || !validImage.isLt1M) {
        return; // Error messages are already shown in beforeUpload
      } else if (event.file && validImage.isJpgOrPng && validImage.isLt1M) {
        const formData = new FormData();
        formData.append("image", event.file);
        const response = await uploadImage(formData);
        if (response) {
          setImage({ ...image, desktopImage: response.url });
          form.setFieldValue("desktopImage", response.url);
          message.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      console.log(error);
      message.error("Error while uploading image");
    }
  };

  const handleMobileImageUpload = async (event) => {
    try {
      const validImage = beforeUpload(event.file);
      if (!validImage.isJpgOrPng || !validImage.isLt1M) {
        return; // Error messages are already shown in beforeUpload
      } else if (event.file && validImage.isJpgOrPng && validImage.isLt1M) {
        const formData = new FormData();
        formData.append("image", event.file);
        const response = await uploadImage(formData);
        if (response) {
          setImage({ ...image, mobileImage: response.url });
          form.setFieldValue("mobileImage", response.url);
          message.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      console.log(error);
      message.error("Error while uploading image");
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    const isLt1M = file.size / 1024 / 1024 <= 1;

    if (!isJpgOrPng) {
      message.error({
        content: 'You can only upload JPG, PNG, or Webp file formats!',
        style: {
          marginTop: '20px',
        },
        duration: 4,
      });
    }
    
    if (!isLt1M) {
      // Calculate actual size in MB with 2 decimal places
      const actualSize = (file.size / 1024 / 1024).toFixed(2);
      message.error({
        content: `Image size (${actualSize} MB) exceeds the 1 MB limit. Please select a smaller one.`,
        style: {
          marginTop: '20px',
        },
        duration: 4,
      });
    }
    
    return { isJpgOrPng, isLt1M };
  };

  const handleClickActionChange = (e) => {
    setClickActionType(e.target.value);
    // Clear related fields when changing click action
    if (e.target.value === "third-party-link") {
      form.setFieldsValue({ vendorDealId: undefined });
    } else if (e.target.value === "vendor-deal") {
      form.setFieldsValue({ thirdPartyLink: undefined });
    }
  };

  const onFinish = async (values) => {
    const formattedDate = values.timer
      ? dayjs(values.timer).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      : null;
    const payload = {
      ...values,
    };
    if (showTimer) {
      payload.timer = formattedDate;
    }

    // Handle click action specific fields
    if (values.clickActionType === "third-party-link") {
      payload.thirdPartyLink = values.thirdPartyLink;
      delete payload.vendorDealId;
    } else if (values.clickActionType === "vendor-deal") {
      payload.vendorDealId = values.vendorDealId;
      delete payload.thirdPartyLink;
    }

    if (isEdit) {
      const res = await updateDealBanner(defaultValues._id, payload);
      if (res) {
        form.resetFields(["image", "position", "timer"]);
        initialValues = {};
        message.success(
          `Flash Deal Banner ${isEdit ? "updated" : "created"} successfully`
        );
        setIsEdit(false);
        setShowForm(false);
      }
    } else {
      const res = await createDealBanner(payload);
      if (res) {
        setShowForm(false);
        form.resetFields();
        message.success(
          `Flash Deal Banner ${isEdit ? "updated" : "created"} successfully`
        );
      }
    }
  };

  useEffect(() => {
    if (isEdit && defaultValues?.timer) {
      setShowTimer(true);
    }
    if (isEdit && defaultValues?.clickActionType) {
      setClickActionType(defaultValues.clickActionType);
    }
  }, []);

  return (
    <div>
      <Form
        onFinish={onFinish}
        layout="vertical"
        form={form}
        initialValues={initialValues}
      >
        <div className="flex">
          <Form.Item
            label="Upload Image for Desktop"
            name="desktopImage"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleImageUpload}
              accept="image/jpeg,image/png,image/webp"
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>
          {image?.desktopImage || defaultValues?.desktopImage ? (
            <img
              src={image?.desktopImage || defaultValues?.desktopImage}
              alt="uploaded"
              style={{ width: "100%" }}
              className="object-contain"
            />
          ) : (
            ""
          )}
        </div>

        <div className="flex">
          <Form.Item
            label="Upload Image for Mobile"
            name="mobileImage"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleMobileImageUpload}
              accept="image/jpeg,image/png,image/webp"
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>
          {image?.mobileImage || defaultValues?.mobileImage ? (
            <img
              src={image?.mobileImage || defaultValues?.mobileImage}
              alt="uploaded"
              style={{ width: "100%" }}
              className="object-contain"
            />
          ) : (
            ""
          )}
        </div>

        <Form.Item
          name="position"
          className="w-[400px] bg-inputFieldGrey rounded-md"
          label="Position"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Dropdown
            placeholder={"Position"}
            defaultValue={defaultValues?.position}
            className="w-[400px] bg-inputFieldGrey focus:bg-white focus:border-primaryBlue hover:border-primaryBlue"
            data={[
              { _id: "first", name: "First" },
              { _id: "second", name: "Second" },
              { _id: "third", name: "Third" },
            ]}
          />
        </Form.Item>

        <Checkbox
          checked={defaultValues?.timer ? true : false}
          onChange={(e) => setShowTimer(e.target.checked)}
        >
          Show Timer
        </Checkbox>

        {showTimer ? (
          <Form.Item name="timer" label="Timer End Date">
            <DatePicker
              placeholder="Timer End Date"
              format="YYYY-MM-DD HH:mm:ss"
              showTime={{
                defaultValue: dayjs("00:00:00", "HH:mm:ss"),
              }}
              className="w-[400px] h-5.4 focus:bg-white focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>
        ) : (
          ""
        )}

        <Form.Item
          name="clickActionType"
          label="Click Action Type"
          rules={[
            {
              required: true,
              message: "Please select click action type",
            },
          ]}
        >
          <Radio.Group onChange={handleClickActionChange} value={clickActionType}>
            <Radio value="third-party-link">Third Party Link</Radio>
            <Radio value="vendor-deal">Vendor Deal</Radio>
          </Radio.Group>
        </Form.Item>

        {clickActionType === "third-party-link" && (
          <Form.Item
            name="thirdPartyLink"
            label="Third Party Link URL"
            className="w-[400px] bg-inputFieldGrey rounded-md"
            rules={[
              {
                required: true,
                message: "Please enter the link URL",
              },
              {
                type: "url",
                message: "Please enter a valid URL",
              },
            ]}
          >
            <Input
              placeholder="Enter link URL (e.g., https://example.com)"
              className="w-[400px] bg-inputFieldGrey focus:bg-white focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>
        )}

        {clickActionType === "vendor-deal" && (
          <Form.Item
            name="vendorDealId"
            label="Vendor Deal ID"
            className="w-[400px] bg-inputFieldGrey rounded-md"
            rules={[
              {
                required: true,
                message: "Please enter the vendor deal ID",
              },
            ]}
          >
            <Input
              placeholder="Enter vendor deal ID"
              className="w-[400px] bg-inputFieldGrey focus:bg-white focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>
        )}

        <Form.Item>
          <Button
            color={colors.primaryBlue}
            title="Save"
            htmlType="submit"
            className="w-full"
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default DealBannerForm;
